import type { Messages } from '../messages.js'

/**
 * Georgian. `სვლა` for flips, the word a Georgian board game uses for a move.
 *
 * Georgian has no capital letters, which is the thing the alphabet's fold exists to protect:
 * Unicode gave Mkhedruli an upper case for headings and `toUpperCase` will use it, so anything
 * that upper-cases Georgian ends up shouting in Mtavruli. Nothing here is upper-cased.
 *
 * After a numeral the noun stays singular — `2 სიტყვა`, not `2 სიტყვები` — so both plural forms
 * carry the same string.
 */
export const ka: Messages = {
  tag: 'ka',

  readingDictionary: 'იკითხება ლექსიკონი…',
  noWordList: '„{language}“-სთვის სიტყვების სია არ არის. შექმენით:  pnpm dictionary build',
  emptyWordList: '„{language}“-ის სიტყვების სია ცარიელია.',

  flips: 'სვლები',
  score: 'ქულები',
  words: 'სიტყვები',
  round: 'რაუნდი',
  ticksLeftLabel: 'ამ რაუნდში დარჩენილი დრო',
  typeAWord: 'აკრიფეთ სიტყვა',
  tapPrompt: 'შეეხეთ ასოებს ასარჩევად ან დასაბრუნებლად, შემდეგ {action}',

  boardOfTiles: 'დაფა {n} კრამიტით',
  faceDown: 'პირქვე',
  wildCard: 'ჯოკერი',
  wildKey: 'ნებისმიერი ასო',
  letterReplaced: '{from} გახდა {to}',
  letterSwap: 'ასოების გაცვლა!',
  spentTile: 'გამოყენებული კრამიტი',
  hiddenWhilePaused: 'პაუზისას დამალულია',
  letterInWord: '{letter}, სიტყვის {position}-ე ასო',

  completeWord: 'სიტყვის დასრულება',

  completeShort: 'დასრულება',
  reset: 'გასუფთავება',
  pause: 'პაუზა',
  resume: 'გაგრძელება',
  newGame: 'ახალი თამაში',
  paused: 'შეჩერებულია',
  outOfFlips: 'სვლები ამოიწურა',
  finalResult: '{score} {words}-ისთვის {rounds}-ში',
  playAgain: 'ისევ თამაში',
  share: 'გაზიარება',
  shareCopied: 'დაკოპირდა.',
  shareSelect: 'დააკოპირეთ ეს:',

  lettersSelect: 'ასოები ირჩევენ',
  keysWild: 'აიღება, როცა აკრეფთ ასოს, რომელიც არცერთ კრამიტზე არ ჩანს',
  clearsEvery: 'ასუფთავებს ყველა არჩეულ {letter}-ს',
  undoLastLetter: 'ბოლო ასოს დაბრუნება',
  noWordsYet: 'ჯერ სიტყვები არ არის.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'უკვე ნაპოვნია',
  reasonTooShort: 'ძალიან მოკლეა',
  reasonNotAWord: 'ეს სიტყვა არ არის',
  reasonAllFound: 'უკვე ყველა გაქვთ',
  noSuchLetterUp: 'გადმობრუნებული {letter} არ არის',
  nothingUp: 'არაფერია გადმობრუნებული',
  letterHidden: 'ერთი ასო დაიმალა',
  shuffled: 'აირია',
  shuffledAndBilled: 'აირია, ჩამოგეჭრათ {flips} გამოუყენებელი',
  tooFewLetters: 'დარჩენილი ასოები ძალიან ცოტაა',

  gameLanguage: 'ენა',
  interfaceLanguage: 'ინტერფეისი',
  dictionarySize: '{full} სიტყვიდან {common} ყოველდღიური',
  filterLanguages: 'ენის ძებნა',
  noMatches: 'დამთხვევა არ არის',

  nerdMode: 'მცოდნეთა რეჟიმი',
  rules: 'წესები',
  difficulty: 'სირთულე',
  difficultyNames: { easy: 'მარტივი', medium: 'საშუალო', hard: 'რთული', insane: 'გიჟური' },
  pausedNoBoard: 'ეს თამაში რეიტინგში არ მოხვდება.',
  about: 'შესახებ',
  themeLabel: 'თემა',
  themeNames: { traditional: 'ტრადიციული', light: 'ნათელი', contrast: 'მაღალი კონტრასტი' },
  tiles: 'კრამიტები (N)',
  secondsPerTick: 'წამი / ტაქტი',
  holdTicks: 'შეჩერების ტაქტები',
  minWord: 'უმოკლესი სიტყვა',
  startingFlips: 'საწყისი სვლები',
  wildChance: 'ჯოკერის შანსი',
  replaceChance: 'ასოს გაცვლის შანსი',
  hideChance: 'ასოს დამალვის შანსი',
  wordCompleteMode: 'სიტყვის დასრულებისას',
  wordCompleteNames: { shuffle: 'არევა', spend: 'დახარჯვა', keep: 'დატოვება' },
  flipEconomy: 'სვლების დაბრუნება',
  flipEconomyNames: {
    none: 'არცერთი',
    perLetter: 'ასოზე',
    fibonacci: 'ფიბონაჩი',
    overMinimum: 'მინიმუმზე მეტი',
  },
  repeatedLetterKey: 'გამეორებული ასოს კლავიში',
  keySchemeNames: { cycle: 'ციკლი', advance: 'წინსვლა' },
  keySchemeHelp: {
    cycle:
      'ა იღებს შემდეგ გამოუყენებელ ა-ს, და როცა ყველა სიტყვაშია, ასუფთავებს მათ. ' +
      'Shift+ა ასევე ასუფთავებს.',
    advance: 'ა იღებს შემდეგ გამოუყენებელ ა-ს. Shift+ა ასუფთავებს სიტყვაში ყველა ა-ს.',
  },

  whatThatMeans: 'რას ნიშნავს ეს',
  factRound: 'რაუნდი',
  factWholeBoardUp: 'მთელი დაფა ღიაა',
  factRoundCosts: 'ერთი რაუნდი ღირს',
  factFlipsBuy: 'საწყისი სვლები ყიდულობს',
  factThisBoard: 'ეს დაფა',
  factBoardHadToAdmit: 'დაფას უნდა დაეშვა',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, უგრძესი {longest}',
  wordsIncludingOneOf: '{words}, მათ შორის ერთი {ceiling}',
  scorelessRounds: '{rounds} ქულის გარეშე',

  whatAWordPays: 'რას აძლევს სიტყვა',
  columnLetters: 'ასოები',
  columnCost: 'ფასი',
  columnPoints: 'ქულები',
  columnFlips: 'სვლები',
  columnNet: 'სხვაობა',

  canonicalRules: '{difficulty} დონის ჩვეული წესები.',
  customRules: 'შეცვლილია ნაგულისხმევიდან. საკუთარი წესებით ქულები რეიტინგში არ ხვდება.',
  applyAndStart: 'გამოყენება და ახალი თამაშის დაწყება',
  changesNextGame: 'ცვლილებები შემდეგი თამაშიდან ამოქმედდება.',
  presets: 'ნაგულისხმევები:',

  start: 'დაწყება',
  restart: 'თავიდან',
  quit: 'გასვლა',
  quitTitle: 'დატოვებთ ამ თამაშს?',
  restartTitle: 'თავიდან დაიწყებთ ამ თამაშს?',
  restartConfirm: 'თავიდან',
  quitConfirm: 'გასვლა',
  keepPlaying: 'თამაშის გაგრძელება',
  personalBest: 'თქვენი საუკეთესო თამაშები',
  newPersonalBest: 'ახალი პირადი რეკორდი.',
  columnRank: '#',
  notRanked: 'საკუთარი წესები, ამიტომ ეს თამაში რეიტინგში არ ხვდება.',
  notRankedPaused: 'დრო გაჩერდა, ამიტომ ეს თამაში რეიტინგში არ ხვდება.',
  rankOfTotal: '{total}-დან {rank}',

  howToPlay: 'როგორ ვითამაშოთ',

  backToGame: 'თამაშში დაბრუნება',
  welcomeTitle: 'კეთილი იყოს თქვენი მობრძანება Blinkered-ში',
  tutorialSkip: 'გამოტოვება',
  tutorialNext: 'შემდეგი',
  tutorialBack: 'უკან',
  tutorialStart: 'თამაშის დაწყება',
  tutorialHideAgain: 'აღარ მაჩვენო',
  tutorialProgress: '{total}-დან {n}',
  tutorialSkipTitle: 'გამოტოვებთ გაცნობას?',
  tutGoalTitle: 'Blinkered რბოლაა',
  tutGoalBody:
    'თამაშს იწყებთ სვლების ფიქსირებული რაოდენობით, და ყოველი გადმობრუნებული კრამიტი ერთ სვლას ღირს. როცა სვლები ამოიწურება, თამაში მთავრდება. სიტყვები სვლებს მოგიტანთ, და რაც უფრო გრძელია სიტყვა, მით მეტ სვლას იღებთ.',
  tutSpentBody:
    'სიტყვაში გამოყენებული ასოები შემდეგ რაუნდამდე პირქვე ბრუნდება. დაფაზე ასოებს მხოლოდ ასოს შეცვლა ცვლის.',
  tutChangeTitle: 'ასოები შეიძლება შეიცვალოს',
  tutSameLetters:
    'შენიშნეთ სიტყვა, რომლის თამაშიც ვერ მოასწარით? იგივე ასოები შემდეგ რაუნდში ბრუნდება, ახალ ადგილებზე არეული.',
  tutPickLetters: 'შეეხეთ სასურველ ასოებს რიგის მიხედვით, რომ სიტყვა შეადგინოთ.',
  tutMoreTurn: 'კრამიტები ფიქრის დროსაც ბრუნდება, ასე რომ უკეთესი ასო ჯერ კიდევ შეიძლება მოვიდეს.',
  tutTapBack:
    'აიღეთ ასო, რომელიც არ გინდოდათ? კიდევ ერთხელ შეეხეთ და დაბრუნდება. ნებისმიერი, არა მხოლოდ ბოლო.',
  tutComplete: 'როცა სიტყვა მზადაა, დააჭირეთ დასრულებას.',
  htBoardTitle: 'დაფა',
  htBoardBody:
    'კრამიტები სათითაოდ ბრუნდება, შემთხვევითი მიმდევრობით. გადმობრუნებულებისგან სიტყვები იდგმება.',
  htWordsTitle: 'სიტყვები',
  htWordsBody:
    'შეადგინეთ სიტყვა გადმობრუნებული კრამიტებისგან ასოების რიგით აკრეფით ან დაწკაპუნებით.',
  htFlipsTitle: 'სვლები',
  htFlipsBody:
    'ყოველი გადმობრუნებული კრამიტი ერთ სვლას ღირს. დასრულებული სიტყვა სვლებს აბრუნებს, გრძელი სიტყვები კი მეტს. როცა სვლები ამოიწურება, თამაში მთავრდება.',
  htRoundTitle: 'რაუნდი',
  htRoundBody:
    'როცა რაუნდის ბოლო კრამიტი გადმობრუნდება, მთელი დაფა წამით ჩერდება. შემდეგ კრამიტები უკუბრუნდება და აირევა, და ახალი რაუნდი იწყება.',
  htLanguagesTitle: 'ენები',
  htLanguagesBody:
    '{n} ენა. ყოველი დაფა იხსნება სიტყვებით, რომლებსაც ხალხი ნამდვილად იყენებს. იშვიათი სიტყვაც ქულას იძლევა, თუ ლექსიკონმა იცის.',
  htKeysTitle: 'კლავიატურა',
  htWildTitle: 'ჯოკერები',
  htWildBody:
    'ზოგჯერ ასოს ნაცვლად ჯოკერი ჩნდება. ჯოკერი ჩაითვლება ნებისმიერ ასოდ, რომელიც სწორ სიტყვას ქმნის. უკვე ნაპოვნი სიტყვა არ ითვლება.',
  htHideTitle: 'ასოები, რომლებიც იმალება',
  htHideBody:
    'უფრო რთულ დონეებზე ღია ასო თავისით შეიძლება უკან გადაბრუნდეს და მოგვიანებით იმავე რაუნდში დაბრუნდეს. ტაიმერი ამისთვის ერთ დამატებით ნიშანს იღებს, წითლად. უკვე არჩეული ასოები უკან არ გადაბრუნდება.',
  tutHideBody:
    'უფრო რთულ დონეებზე ღია ასო თავისით შეიძლება უკან გადაბრუნდეს და მოგვიანებით იმავე რაუნდში დაბრუნდეს. უკვე არჩეული ასოები უკან არ გადაბრუნდება.',
  htSwapTitle: 'ასოები, რომლებიც იცვლება',
  htSwapBody:
    'ზოგჯერ რაუნდებს შორის ერთი ასო სხვით იცვლება. დაინახავთ, რომელი წავიდა და რომელი მოვიდა.',
  htLevelsTitle: 'დონეები',
  htLevelEasy:
    'იგივე თორმეტი ასო მთელი თამაშის განმავლობაში, ასე რომ შეგიძლიათ დაისწავლოთ და სიტყვების სია თავში ატაროთ. კრამიტები ნელა ბრუნდება და მთელი დაფა საკმარისად დიდხანს ჩანს, რომ არჩევანი დაასრულოთ.',
  htLevelMedium:
    'დროდადრო ერთი ასო იცვლება, ასე რომ უფრო ძნელია გახსოვდეთ სიტყვები, რომლებიც მოგვიანებისთვის გადადეთ. ნაკლები დრო საყურებლად და ნაკლები საფიქრალად.',
  htLevelHard:
    'სამასოიანი სიტყვები აღარ ითვლება, და ასო დაახლოებით ყოველ მეორე რაუნდში იცვლება. დაფა ძლივს ჩნდება, უკვე ირევა.',
  htLevelInsane:
    'ყველაფერი ერთდროულად, სრული სისწრაფით. დაფა თითქმის მაშინვე ირევა ბოლო სვლის შემდეგ.',
  htTouchTitle: 'სენსორული ეკრანი',
  htTouchBody:
    'შეეხეთ გადმობრუნებულ კრამიტს, რომ მისი ასო აიღოთ. შეეხეთ აღებულ ასოს, რომ დააბრუნოთ. დასრულება და გასუფთავება დაფის ქვეშაა.',

  accountTitle: 'თქვენი ანგარიში',
  menuProfile: 'ჩემი პროფილი',
  menuGames: 'ჩემი თამაშები',
  menuPublicPage: 'ჩემი საჯარო გვერდი',
  menuSignedInAs: 'შესული ხართ როგორც',
  signIn: 'შესვლა',
  signOut: 'გასვლა',
  signInTitle: 'შედით ან დარეგისტრირდით',
  signInLead: 'ანგარიში ინახავს თქვენს თამაშებს, ყველა მოწყობილობაზე.',
  signInKeepGame: 'შედით, რომ ეს თამაში დარჩეს, და ყველა შემდეგი.',
  continueWithApple: 'გაგრძელება Apple-ით',
  continueWithGoogle: 'გაგრძელება Google-ით',
  signInOr: 'ან',
  emailLabel: 'ელფოსტა',
  codeLabel: 'კოდი',
  emailMeACode: 'გამომიგზავნეთ კოდი',
  working: 'ერთი წამი…',
  sendNewCode: 'ახალი კოდის გაგზავნა',
  codeSent: 'შეამოწმეთ ელფოსტა. კოდი 10 წუთს მოქმედებს.',
  notNow: 'ახლა არა',
  badEmail: 'ეს ელფოსტის მისამართს არ ჰგავს.',
  badCode: 'ეს კოდი არ გამოდგა. კოდები 10 წუთში ვადაგასულია და ერთხელ მუშაობს.',
  serverBusy: 'სერვერს ვერ მივწვდით. სცადეთ ცოტა ხანში.',
  serverDown: 'სერვერს ვერ მივწვდით.',
  offline: 'ხაზგარეშე',
  leaderboardTitle: 'რეიტინგი',
  leaderboardEmpty: 'ამ რეიტინგში ჯერ თამაშები არ არის. იყავი პირველი.',
  leaderboardWouldBe: 'სად იქნებოდა ეს თამაში',
  leaderboardThisGame: 'ეს თამაში',
  leaderboardRanksHere: 'სად არის ეს თამაში',
  leaderboardOutside: 'პირველი ხუთეულის გარეთ.',
  tutAccountTitle: 'შეინახე შენი ქულები',
  tutAccountBody: 'ანგარიში ინახავს შენს თამაშებს, დებს ქულებს რეიტინგში და ინახავს შენს პროფილს.',
  ssoCancelled: 'შესვლა გაუქმდა.',
  ssoExpired: 'ძალიან დიდხანს გაგრძელდა. შედით ხელახლა.',
  ssoNoUsername: 'ანგარიშის შექმნა ვერ დასრულდა. სცადეთ ცოტა ხანში.',
  ssoFailed: 'ვერ გამოვიდა. სცადეთ ხელახლა, ან გამოიყენეთ ელფოსტა.',
  tabProfile: 'პროფილი',
  countryAny: 'არ ვიტყვი',
  findCountry: 'ქვეყნების ძებნა',
  usernameLabel: 'მომხმარებლის სახელი',
  bioLabel: 'ჩემ შესახებ',
  countryLabel: 'ქვეყანა',
  bioHint: 'ბმულების გარეშე. {left}/{max}',
  save: 'შენახვა',
  saving: 'ინახება…',
  saved: 'შენახულია.',
  nameTaken: 'ეს სახელი უკვე დაკავებულია.',
  nameTooShort: 'ცოტა უფრო გრძელი, თუ შეიძლება.',
  nameTooLong: 'ძალიან გრძელია.',
  nameBadCharacters: 'მხოლოდ ასოები, ციფრები, დეფისები და ქვედა ტირეები.',
  nameBadEdges: 'უნდა იწყებოდეს და მთავრდებოდეს ასოთი ან ციფრით.',
  nameMixedScripts: 'აირჩიეთ ერთი ანბანი და დარჩით მასში.',
  nameReserved: 'ეს ჰგავს სახელს, რომელსაც ჩვენ ვარიგებთ. აირჩიეთ სხვა.',
  nameUnusable: 'ეს სახელი არ გამოდგება.',
  bioTooLong: 'აქ მაქსიმუმ {max} სიმბოლო ეტევა.',
  bioHasLink: 'აქ ბმულები არ დაიშვება.',
  bioHasControl: 'შეიცავს სიმბოლოებს, რომლებიც აქ ვერ იქნება.',
  bioUnusable: 'ეს ტექსტი არ გამოდგება.',
  gamesHeading: 'თამაშები',
  gamesEmpty: 'აქ ჯერ არაფერია. შესული სათამაშო თამაშები აქ გამოჩნდება.',
  gamesEmptyShort: 'აქ ჯერ არაფერია.',
  gamesLoading: 'იკითხება თქვენი თამაშები…',
  theirGamesLoading: 'იკითხება მათი თამაშები…',
  profileLoading: 'იკითხება პროფილი…',
  playerNotFound: 'აქ ამ სახელით ვერავინ არის.',
  columnWhen: 'როდის',
  columnRounds: 'რაუნდი',
  columnGame: 'თამაში',
  gameLoading: 'იკითხება თამაში…',
  gameUnreadable: 'ეს თამაში ვერ წაიკითხა.',
  gameNotRanked: 'შეცვლილი წესებით ნათამაშები, ამიტომ არ ითვლება.',
  detailNotKept: 'ამ თამაშის სიტყვები და დაფები აღარ ინახება.',
  openingBoard: 'საწყისი დაფა',
  roundByRound: 'რაუნდ-რაუნდ',
  roundNothing: 'ვერაფერი მოიძებნა',
  changeReplaced: '{letter} შეიცვალა უჯრაში {slot}',
  changeWildOn: '{letter} ჯოკერი უჯრაში {slot}',
  changeWildOff: '{letter} ჯოკერი წავიდა უჯრიდან {slot}',
  keepThisGame: 'შეინახე ეს თამაში',
  savedToYourGames: 'შენახულია თქვენს თამაშებში.',

  reportThis: 'მოხსენება',
  reportHeading: 'ამის მოხსენება',
  reportTheName: 'სახელი',
  reportTheBio: 'აღწერა',
  reportTheScore: 'ქულა',
  reportWhy: 'რა არის მასში არასწორი? სურვილისამებრ.',
  reportSend: 'მოხსენების გაგზავნა',
  reportSent: 'გმადლობთ. ვინმე გადახედავს.',
  reportSignIn: 'შედით, რომ რამე მოახსენოთ.',
  reportGone: 'ეს აღარ არსებობს.',
  reportCancel: 'გაუქმება',

  deleteAccount: 'ჩემი ანგარიშის წაშლა',
  deleteAccountWhat:
    'ეს სამუდამოდ შლის თქვენს პროფილს და ყველა შენახულ თამაშს. დაბრუნება შეუძლებელია.',
  deleteAccountAsk: 'გამომიგზავნეთ კოდი',
  deleteConfirm: 'სამუდამოდ წაშლა',
  deleteGone: 'თქვენი ანგარიში წაიშალა. გმადლობთ თამაშისთვის.',
  deleteNoAddress:
    'ამ ანგარიშზე დადასტურებული მისამართი არ არის, ამიტომ წაშლის შემოწმება ვერ ხდება. დაგვიკავშირდით და ხელით გავაკეთებთ.',

  rulesetFacts: 'სიტყვები {letters} · ახალი კრამიტი ყოველ {seconds}',
  plurals: {
    words: { one: '{n} სიტყვა', other: '{n} სიტყვა' },
    rounds: { one: '{n} რაუნდი', other: '{n} რაუნდი' },
    flips: { one: '{n} სვლა', other: '{n} სვლა' },
    ticks: { one: '{n} ტაქტი', other: '{n} ტაქტი' },
    points: { one: '{n} ქულა', other: '{n} ქულა' },
    minLetters: { one: 'მინიმუმ {n} ასოსგან', other: 'მინიმუმ {n} ასოსგან' },
    seconds: { one: '{n} წამში', other: '{n} წამში' },
  },
}
